/**
 * ShareButton.tsx - "Share" control for the chat header.
 *
 * Shows only when an existing chat is open (postId set). Generates a public
 * read-only link (ChatGPT-style), lets the author copy it or unshare. Author-
 * only enforcement lives on the backend; here we just surface the action.
 */

import { useState } from 'react'
import {
    Button,
    IconButton,
    Input,
    Text,
    HStack,
    Spinner,
    TooltipContent,
    TooltipPositioner,
    TooltipRoot,
    TooltipTrigger,
    Portal,
    DialogRoot,
    DialogBackdrop,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogTitle,
    DialogCloseTrigger,
} from '@chakra-ui/react'
import { LuShare2 } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { useChatContext } from '@/contexts/ChatContext'
import { useShareChat } from '@/hooks/useShareChat'
import { toaster } from '@/ui/components/ui/toaster'

export const ShareButton: React.FC = () => {
    const { t, i18n } = useTranslation()
    const isRTL = i18n.dir() === 'rtl'
    const { postId, parentCircleId } = useChatContext()
    const { loading, shareUrl, createShare, revokeShare, reset } = useShareChat()
    const [open, setOpen] = useState(false)

    // Only meaningful for an existing chat.
    if (!postId) return null

    const handleOpen = async () => {
        setOpen(true)
        try {
            await createShare(postId, parentCircleId)
        } catch {
            toaster.create({ title: t('share_error', 'Something went wrong. Please try again.'), type: 'error', duration: 4000 })
            setOpen(false)
        }
    }

    const handleClose = () => {
        setOpen(false)
        reset()
    }

    const handleCopy = async () => {
        if (!shareUrl) return
        try {
            await navigator.clipboard.writeText(shareUrl)
            toaster.create({ title: t('link_copied', 'Link copied to clipboard'), type: 'success', duration: 2500 })
        } catch {
            toaster.create({ title: t('share_error', 'Something went wrong. Please try again.'), type: 'error', duration: 4000 })
        }
    }

    const handleUnshare = async () => {
        try {
            await revokeShare()
            toaster.create({ title: t('link_revoked', 'Sharing disabled'), type: 'success', duration: 2500 })
        } catch {
            toaster.create({ title: t('share_error', 'Something went wrong. Please try again.'), type: 'error', duration: 4000 })
        }
        handleClose()
    }

    return (
        <>
            <TooltipRoot openDelay={500} positioning={{ placement: 'bottom' }}>
                <TooltipTrigger asChild>
                    <IconButton
                        aria-label={t('share_chat', 'Share')}
                        variant="ghost"
                        size="sm"
                        width="36px"
                        height="36px"
                        color="#333333"
                        _hover={{ background: 'var(--cair-accent-subtle)' }}
                        onClick={handleOpen}
                    >
                        <LuShare2 />
                    </IconButton>
                </TooltipTrigger>
                <Portal>
                    <TooltipPositioner>
                        <TooltipContent>{t('share_chat', 'Share')}</TooltipContent>
                    </TooltipPositioner>
                </Portal>
            </TooltipRoot>

            <DialogRoot open={open} onOpenChange={(e) => !e.open && handleClose()}>
                <DialogBackdrop css={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.5)' }} />
                <DialogContent
                    dir={i18n.dir()}
                    css={{
                        position: 'fixed',
                        top: '40%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999,
                        background: 'white',
                        borderRadius: '8px',
                        padding: '16px',
                        width: 'min(92vw, 460px)',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    }}
                >
                    <DialogHeader>
                        <DialogTitle textAlign={isRTL ? 'right' : 'left'} css={{ fontSize: '16px' }}>
                            {t('share_chat', 'Share')}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <Text fontSize="sm" color="gray.600" marginBottom={3} textAlign={isRTL ? 'right' : 'left'}>
                            {t('share_chat_description', 'Anyone with this link can view this conversation.')}
                        </Text>
                        {loading || !shareUrl ? (
                            <HStack justify="center" paddingY={4}><Spinner /></HStack>
                        ) : (
                            <HStack gap={2} flexDirection={isRTL ? 'row-reverse' : 'row'}>
                                <Input value={shareUrl} readOnly fontSize="sm" dir="ltr" onFocus={(e) => e.currentTarget.select()} />
                                <Button onClick={handleCopy} flexShrink={0}>{t('copy_link', 'Copy link')}</Button>
                            </HStack>
                        )}
                    </DialogBody>
                    <DialogFooter css={{ justifyContent: 'space-between', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                        <Button variant="ghost" colorScheme="red" onClick={handleUnshare} disabled={!shareUrl}>
                            {t('unshare', 'Delete link')}
                        </Button>
                        <Button variant="outline" onClick={handleClose}>{t('close', 'Close')}</Button>
                    </DialogFooter>
                    <DialogCloseTrigger />
                </DialogContent>
            </DialogRoot>
        </>
    )
}
